import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi, type Catalog, type Collection, type CollectionRule } from '../../api/catalog';

export function SmartCollectionBuilderPage() {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [catalogId, setCatalogId] = useState('');
  const [name, setName] = useState('');
  const [rules, setRules] = useState<CollectionRule[]>([{ field: 'brand', operator: 'equals', value: '' }]);
  const [error, setError] = useState('');

  const reload = () => {
    void catalogApi
      .listCollections(catalogId ? { catalog_id: catalogId, type: 'smart' } : { type: 'smart' })
      .then(setCollections)
      .catch((e) => setError(String(e)));
  };

  useEffect(() => {
    void catalogApi.listCatalogs().then((items) => {
      setCatalogs(items);
      if (items[0]?.id) setCatalogId(items[0].id);
    });
  }, []);

  useEffect(() => {
    reload();
  }, [catalogId]);

  const addRule = () => setRules((r) => [...r, { field: 'category', operator: 'equals', value: '' }]);

  const updateRule = (idx: number, patch: Partial<CollectionRule>) => {
    setRules((r) => r.map((rule, i) => (i === idx ? { ...rule, ...patch } : rule)));
  };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await catalogApi.createCollection({
        catalog_id: catalogId || undefined,
        name: name.trim(),
        type: 'smart',
        rules: rules.filter((r) => r.value.trim()),
      });
      setName('');
      setRules([{ field: 'brand', operator: 'equals', value: '' }]);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/catalog/collections" className="text-sm text-slate-600 hover:underline">← Collections</Link>
      <h1 className="mt-4 text-2xl font-semibold">Smart Collection Builder</h1>
      <p className="mt-1 text-sm text-slate-600">Dynamic collections driven by rules.</p>
      <div className="mt-6 space-y-4 rounded-xl border bg-white p-6">
        <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm">
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Collection name" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <div className="space-y-2">
          <p className="text-sm font-medium">Rules</p>
          {rules.map((rule, idx) => (
            <div key={idx} className="flex flex-wrap gap-2">
              <select value={rule.field} onChange={(e) => updateRule(idx, { field: e.target.value })} className="rounded-lg border px-2 py-1 text-sm">
                <option value="brand">Brand</option>
                <option value="category">Category</option>
                <option value="tag">Tag</option>
                <option value="price">Price</option>
              </select>
              <select value={rule.operator} onChange={(e) => updateRule(idx, { operator: e.target.value })} className="rounded-lg border px-2 py-1 text-sm">
                <option value="equals">Equals</option>
                <option value="contains">Contains</option>
                <option value="gt">Greater than</option>
                <option value="lt">Less than</option>
              </select>
              <input
                value={rule.value}
                onChange={(e) => updateRule(idx, { value: e.target.value })}
                placeholder="Value"
                className="min-w-[120px] flex-1 rounded-lg border px-2 py-1 text-sm"
              />
            </div>
          ))}
          <button type="button" onClick={addRule} className="text-sm text-slate-700 hover:underline">+ Add rule</button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
          Create smart collection
        </button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
        <table className="min-w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Rules</th>
            </tr>
          </thead>
          <tbody>
            {collections.map((col) => (
              <tr key={col.id} className="border-b">
                <td className="px-4 py-3 font-medium">{col.name}</td>
                <td className="px-4 py-3">{col.rules?.map((r) => `${r.field} ${r.operator} ${r.value}`).join('; ') ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
